import {HttpClient, HttpContext, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {JwtService} from '../jwt.service';
import {PedidoFlex} from '../persistences/pedido-flex';
import { PedidoSimplificado, PedidoSimplificadoFiltros } from '../persistences/pedido-simplificado';
import {PedidoVenda} from '../persistences/pedido-venda';
import {TipoEntrega} from '../persistences/tipo-entrega';
import {ConfigService} from './config.service';
import { VendaPerdida } from '../persistences/venda-perdida';
import { CobrancaImediataPix } from '../persistences/cobranca-imediata-pix';
import { PixResponse } from '../persistences/pix-response';
import { SUPRIMIR_DIALOGO_ERRO } from './http-context-tokens';


/* Retorno de /TipoEntrega/getTransportadorPadraoTipoEntrega */
export interface TransportadorPadrao {
	cdTransportador: number;
}

@Injectable({
  providedIn: 'root'
})
export class PedidoService {

	constructor(
		private configService: ConfigService,
		private httpClient: HttpClient,
		private jwtService: JwtService
	) { }


	condicaoPagamento(cdCliente: number){

		this.jwtService.validarAcessToken();

		let httpParams = new HttpParams();
		httpParams = httpParams.append('cdCliente', cdCliente.toString());
		return this.httpClient.get(
			this.configService.getEndereco() + '/FormaCondPgto/getByCdCliente',
			{ headers: this.configService.getHeaders(),
			params: httpParams
			}
		)
	}


	getByCliente(cdCliente: string): Observable<PedidoVenda[]>{

		this.jwtService.validarAcessToken();

        let httpParams = new HttpParams();
		httpParams = httpParams.append('cdCliente', cdCliente).append('emAberto', 'true').append('origem', 'E');
		return this.httpClient.get<PedidoVenda[]>(
			this.configService.getEndereco() + '/PedidoVenda/pedidosByCliente',
			{ headers: this.configService.getHeaders(),
			params: httpParams
			}
		)
	}

	getPedidosVendaSimplificadoByCliente(filtros: PedidoSimplificadoFiltros): Observable<PedidoSimplificado[]>{

		this.jwtService.validarAcessToken();

        let httpParams = new HttpParams();
		httpParams = httpParams
			.append('cdCliente', filtros.cdCliente)
			.append('emAberto', filtros.emAberto)
			.append('origem', filtros.origem)
			.append('dtInicial', filtros.dtInicial)
			.append('dtFinal', filtros.dtFinal)
			.append('situacao', filtros.situacao);
		return this.httpClient.get<PedidoSimplificado[]>(
			this.configService.getEndereco() + '/pedidoVenda/simplificado/getByCliente',
			{ headers: this.configService.getHeaders(),
				params: httpParams
			}
		)
	}

	getTipoEntrega(): Observable<TipoEntrega[]>{

		this.jwtService.validarAcessToken();

		const header = new HttpHeaders({
			'authToken': localStorage.getItem('access_token_business')
		});

		return this.httpClient.get<TipoEntrega[]>(
			this.configService.getEndereco() + '/TipoEntrega/getAll',
			{ headers: header }
		)
	}

	/*
	 * Tipo de frete padrão cadastrado para o tipo de entrega (tabela tipo_entrega_tp_frete).
	 * O endpoint devolve a string crua (ex.: 3, sem aspas) e vazio quando não há cadastro,
	 * por isso responseType 'text' — com o padrão 'json' o valor viria como number.
	 *
	 * SUPRIMIR_DIALOGO_ERRO: a falha aqui tem fallback definido em
	 * PedidoManager.promiseResolverDadosTransporte (mantém tpFrete '9') e é irrelevante para
	 * o comprador. Sem a marca, o interceptor abriria um diálogo modal no meio do Finalizar —
	 * o endpoint responde 400 em qualquer exceção, e 404 se o WEB implantado for anterior a ele.
	 */
	getTpFretePadraoByCdTpEntrega(cdTpEntrega: number): Observable<string>{

		this.jwtService.validarAcessToken();

		let httpParams = new HttpParams();
		httpParams = httpParams.append('cdTpEntrega', cdTpEntrega);
		return this.httpClient.get(
			this.configService.getEndereco() + '/TipoEntrega/getTpFretePadraoByCdTpEntrega',
			{ headers: this.configService.getHeaders(),
			params: httpParams,
			responseType: 'text',
			context: new HttpContext().set(SUPRIMIR_DIALOGO_ERRO, true)
			}
		)
	}

	/*
	 * Transportador padrão do tipo de entrega. Retorna { cdTransportador: N }, ou 0 quando não cadastrado.
	 * SUPRIMIR_DIALOGO_ERRO pelo mesmo motivo do método acima — fallback é transportador 0.
	 */
	getTransportadorPadraoTipoEntrega(cdTpEntrega: number): Observable<TransportadorPadrao>{

		this.jwtService.validarAcessToken();

		let httpParams = new HttpParams();
		httpParams = httpParams.append('cdTpEntrega', cdTpEntrega);
		return this.httpClient.get<TransportadorPadrao>(
			this.configService.getEndereco() + '/TipoEntrega/getTransportadorPadraoTipoEntrega',
			{ headers: this.configService.getHeaders(),
			params: httpParams,
			context: new HttpContext().set(SUPRIMIR_DIALOGO_ERRO, true)
			}
		)
	}

	gravarPedido (pedido: PedidoFlex) {

		this.jwtService.validarAcessToken();

		return this.httpClient.post(
			this.configService.getEndereco() + '/PedidoVendaBusiness/gravarBusiness',
			JSON.stringify(pedido),
			{ headers: this.configService.getHeaders() }
		)
	}

	pedidoVendaPendenteBusiness(cdCliente: number): Observable<PedidoVenda>{

		this.jwtService.validarAcessToken();

		let httpParams = new HttpParams();
		httpParams = httpParams.append('cdCliente', cdCliente.toString());
		return this.httpClient.get<PedidoVenda>(
			this.configService.getEndereco() + '/PedidoVenda/pedidoVendaPendenteBusiness',
			{ headers: this.configService.getHeaders(),
			params: httpParams
			}
		)
	}

    novoPedidoVenda (pedido: PedidoFlex): Observable<PedidoVenda>{

		this.jwtService.validarAcessToken();

		return this.httpClient.post<PedidoVenda>(
			this.configService.getEndereco() + '/PedidoVendaBusiness/novoPedidoVenda',
			JSON.stringify(pedido),
			{ headers: this.configService.getHeaders() }
		)
	}

	getPedidoVendaByIdPedido(IdPedido: number): Observable<PedidoVenda>{
		let httpParams = new HttpParams();
		httpParams = httpParams.append('IdPedido', IdPedido);
		return this.httpClient.get<PedidoVenda>(
			this.configService.getEndereco() + '/PedidoVenda/pedidoById',
			{ headers: this.configService.getHeaders(),
				params: httpParams
			}
		)
	}

	gravarVendaPerdida(vendaPerdida: VendaPerdida): Observable<string>{
		const retorno = this.httpClient.post<string>(
			this.configService.getEndereco() + '/VendaPerdida/gravarVendaPerdida',
			JSON.stringify(vendaPerdida),
			{ 
				headers: this.configService.getHeaders(), 
				responseType:'text'  as 'json'
			}
		)
		return retorno;
	}

	getTiposEntregaCarrinho(): Observable<any>{
		let httpParams = new HttpParams();
		httpParams = httpParams.append('chave', "TIPOS_ENTREGA_BUSINESS_CARRINHO");
		return this.httpClient.get<any>(this.configService.getEndereco()+"/business/config/getById",{
			headers: this.configService.getHeaders(),
			params: httpParams
		});
	}

	cobrancaImediataPix (cobrancaImediataPix: CobrancaImediataPix):Observable<PixResponse>  {
		this.jwtService.validarAcessToken();
		return this.httpClient.post(
			this.configService.getEndereco() + '/flexsmartPix/cobrancaImediata',
			JSON.stringify(cobrancaImediataPix),
			{ headers: this.configService.getHeaders() }
		)
	}

	statusPix (id: string): Observable<PixResponse> {
		this.jwtService.validarAcessToken();
		const httpParams = new HttpParams().append('id', id);
		return this.httpClient.get<PixResponse>(
			this.configService.getEndereco() + '/flexsmartPix/status',
			{ headers: this.configService.getHeaders(), params: httpParams }
		)
	}

	aprovarOrcamento(idGeralOrcamento: number, cdSituacao: number): Observable<any> {
		this.jwtService.validarAcessToken();
		let httpParams = new HttpParams()
			.append('idGeralOrcamento', idGeralOrcamento)
			.append('cdSituacao', cdSituacao);
		return this.httpClient.post(
			this.configService.getEndereco() + '/PedidoVendaBusiness/aprovarOrcamento',
			null,
			{ headers: this.configService.getHeaders(), params: httpParams, responseType: 'text' as 'json' }
		);
	}

}
